import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Link, Boxes, MousePointerClick, Copy, Scissors, ClipboardPaste, CopyPlus, Image, Paintbrush, MousePointer2 } from 'lucide-react';
import type { ContextMenuItem } from '../../components/ContextMenu';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import { generateGroupId } from '../types';
import { getHandler } from '../entities/registry';
import type { CanvasEntity } from '../entityTypes';
import type { CanvasGroup, CanvasSegment, CanvasLayer } from '../types';
import type { ContextMenuState, CanvasPattern } from '../stores/canvasUiStore';
import type { CanvasUiStore } from '../stores/canvasUiStore';
import { useCanvasStores, useCanvasLayers, materializeSegmentsFromStore, materializeGroupsFromStore } from '../stores/CanvasContext';
import { useEdgeContextMenu } from './useEdgeContextMenu';
import { useAddSubmenuItems } from './useAddSubmenuItems';
import { useCanvasClipboardActions } from './useCanvasClipboard';
import { getClipboard } from '../clipboard/clipboardStore';
import { globalTasksStore } from '../../stores/globalTasksStore';
import { buildGroupBoundsMap } from '../lib/buildGroupBoundsMap';
import type { CanvasStore } from '../stores/canvasStore';

export type ContextMenuSetter = (state: ContextMenuState) => void;
export type GroupPickerSetter = (state: { targetNodeIds: string[] } | null) => void;

export { type ContextMenuState } from '../stores/canvasUiStore';

function buildEntityMenuItems(
  entityId: string,
  entity: CanvasEntity,
  store: CanvasStore,
  layers: CanvasLayer[],
  groups: CanvasGroup[],
  segments: CanvasSegment[],
  handlers: {
    handleRemoveSelected: () => void;
    handleCreateGroup: () => void;
    handleGroupToggleCollapse: (groupId: string) => void;
    setGroupPickerState: GroupPickerSetter;
    handleCopy: () => void;
    handleCopyAsImage: () => void;
    handleCut: () => void;
    handleDuplicate: () => void;
    handleActivateDepExplorer: (entityId: string) => void;
  },
  t: (key: any) => string,
): ContextMenuItem[] {
  const sel = canvasSelectionStore.getSnapshot().context;
  const isSelected = sel.selectedEntityIds.includes(entityId);
  const isLocked = layers.some(l => l.id === entity.layerId && l.locked);
  const handler = getHandler(entity.kind);
  const handlerItems = handler.getContextMenuItems({
    store,
    entity,
    layers,
    groups,
    segments,
    selectedEntityIds: sel.selectedEntityIds,
    enteredGroupId: sel.enteredGroupId,
    onRemoveSelected: handlers.handleRemoveSelected,
    onCreateGroup: handlers.handleCreateGroup,
    onGroupToggleCollapse: handlers.handleGroupToggleCollapse,
    setGroupPickerState: handlers.setGroupPickerState,
    onActivateDepExplorer: handlers.handleActivateDepExplorer,
  });

  return [
    ...(!isLocked ? [{
      id: 'toggle-select',
      label: isSelected ? t('canvasMenu.deselect') : t('canvasMenu.select'),
      icon: MousePointerClick,
      iconColor: '#3b82f6',
      onClick: () => {
        canvasSelectionStore.send({ type: 'entity.toggle', entityId });
      },
    }] : []),
    ...handlerItems.length > 0 ? [{ ...handlerItems[0], separator: true }, ...handlerItems.slice(1)] : [],
    {
      id: 'copy',
      label: t('canvasMenu.copy'),
      icon: Copy,
      iconColor: '#6366f1',
      separator: true,
      onClick: handlers.handleCopy,
    },
    {
      id: 'cut',
      label: t('canvasMenu.cut'),
      icon: Scissors,
      iconColor: '#6366f1',
      onClick: handlers.handleCut,
    },
    {
      id: 'duplicate',
      label: t('canvasMenu.duplicate'),
      icon: CopyPlus,
      iconColor: '#6366f1',
      onClick: handlers.handleDuplicate,
    },
    {
      id: 'copy-as-image',
      label: t('canvasMenu.copyAsImage'),
      icon: Image,
      iconColor: '#0ea5e9',
      separator: true,
      onClick: handlers.handleCopyAsImage,
    },
  ];
}

function buildPatternOptions(t: (key: any) => string): { id: CanvasPattern; label: string }[] {
  return [
    { id: 'none', label: t('canvasMenu.patternNone') },
    { id: 'dots', label: t('canvasMenu.patternDots') },
    { id: 'grid', label: t('canvasMenu.patternGrid') },
    { id: 'crosses', label: t('canvasMenu.patternCrossDots') },
    { id: 'graph-paper', label: t('canvasMenu.patternGraph') },
  ];
}

function buildBackgroundSubmenuItems(uiStore: CanvasUiStore, t: (key: string) => string): ContextMenuItem {
  const current = uiStore.getSnapshot().context.canvasPattern;
  const patternOptions = buildPatternOptions(t);
  return {
    id: 'canvas-background',
    label: t('canvasMenu.background'),
    icon: Paintbrush,
    iconColor: '#8b5cf6',
    separator: true,
    children: patternOptions.map(({ id, label }) => ({
      id: `bg-${id}`,
      label: id === current ? `\u25CF ${label}` : label,
      onClick: () => uiStore.send({ type: 'canvasPattern.set', pattern: id }),
    })),
  };
}

function buildCanvasMenuItems(
  store: CanvasStore,
  uiStore: CanvasUiStore,
  addSubmenuItems: ContextMenuItem[],
  handleCreateGroup: () => void,
  handlePaste: () => void,
  t: (key: any) => string,
): ContextMenuItem[] {
  const sel = canvasSelectionStore.getSnapshot().context;
  const hasSelection = sel.selectedEntityIds.length > 0;

  const items: ContextMenuItem[] = [
    {
      id: 'add-entity',
      label: t('canvasMenu.add'),
      icon: Plus,
      iconColor: '#10b981',
      children: addSubmenuItems,
    },
  ];

  if (getClipboard()) {
    items.push({
      id: 'paste',
      label: t('canvasMenu.paste'),
      icon: ClipboardPaste,
      iconColor: '#6366f1',
      onClick: handlePaste,
    });
  }

  if (hasSelection) {
    items.push({
      id: 'group-selection',
      label: t('canvasMenu.group'),
      icon: Boxes,
      iconColor: '#8b5cf6',
      separator: true,
      onClick: handleCreateGroup,
    });
  }

  const ctx = store.getSnapshot().context;
  const raw = Object.values(ctx.nodes);
  const layers = ctx.layers;
  const lockedLayerIds = new Set(layers.filter(l => l.locked).map(l => l.id));
  const existingTaskIds = new Set<string>();
  for (const e of raw) {
    if (e.kind === 'task' && !lockedLayerIds.has(e.layerId)) existingTaskIds.add(e.id);
  }

  if (existingTaskIds.size > 0) {
    items.push({
      id: 'select-all',
      label: t('canvasMenu.selectAllNodes'),
      icon: Link,
      iconColor: '#3b82f6',
      separator: true,
      onClick: () => {
        canvasSelectionStore.send({ type: 'entity.set', entityIds: [...existingTaskIds] });
      },
    });
  }

  items.push(buildBackgroundSubmenuItems(uiStore, t));

  const isClickThrough = uiStore.getSnapshot().context.spriteClickThrough;
  items.push({
    id: 'sprite-clickthrough-global',
    label: isClickThrough ? t('canvasMenu.spriteClickThroughOff') : t('canvasMenu.spriteClickThroughOn'),
    icon: MousePointer2,
    iconColor: '#f59e0b',
    onClick: () => uiStore.send({ type: 'spriteClickThrough.toggle' }),
  });

  return items;
}

export function useCanvasContextMenus() {
  const { t } = useTranslation();
  const { store, uiStore } = useCanvasStores();
  const layers = useCanvasLayers();
  const { buildAddSubmenuItems } = useAddSubmenuItems();
  const { handleCopy, handleCopyAsImage, handleCut, handlePaste, handleDuplicate } = useCanvasClipboardActions();

  const setContextMenu = useCallback((menu: ContextMenuState | null) => {
    uiStore.send({ type: 'contextMenu.set', menu });
  }, [uiStore]);

  const setGroupPickerState = useCallback((state: { targetNodeIds: string[] } | null) => {
    uiStore.send({ type: 'groupPicker.set', state });
  }, [uiStore]);

  const groupPickerState = uiStore.getSnapshot().context.groupPickerState;

  const groupPickerCurrentGroupIds = useMemo(() => {
    if (!groupPickerState) return undefined;
    const tasksById = globalTasksStore.getSnapshot().context.tasksById;
    const groups = materializeGroupsFromStore(store, tasksById);
    return new Set(
      groups.filter(g => groupPickerState.targetNodeIds.every(id => g.memberNodeIds.includes(id))).map(g => g.id),
    );
  }, [groupPickerState, store]);

  const handleRemoveSelected = useCallback(() => {
    const sel = canvasSelectionStore.getSnapshot().context;
    for (const entityId of sel.selectedEntityIds) {
      store.send({ type: 'entity.remove', entityId });
    }
    canvasSelectionStore.send({ type: 'entity.clear' });
  }, [store]);

  const handleGroupToggleCollapse = useCallback((groupId: string) => {
    const ctx = store.getSnapshot().context;
    const rawNodes = Object.values(ctx.nodes);
    const tasksById = globalTasksStore.getSnapshot().context.tasksById;
    const groups = materializeGroupsFromStore(store, tasksById);

    const memberRectMap = new Map<string, { x: number; y: number; width: number; height: number }>();
    for (const e of rawNodes) {
      if (!getHandler(e.kind).hasOwnRect) continue;
      memberRectMap.set(e.id, { x: e.x, y: e.y, width: e.width, height: e.height });
    }
    const groupBoundsMap = buildGroupBoundsMap(groups, memberRectMap, new Set());
    const bounds = groupBoundsMap.get(groupId);
    store.send({
      type: 'group.toggleCollapse',
      groupId,
      collapsedX: bounds?.x,
      collapsedY: bounds?.y,
    });
  }, [store]);

  const handleCreateGroup = useCallback(() => {
    const sel = canvasSelectionStore.getSnapshot().context;
    const groupId = generateGroupId();
    store.send({
      type: 'group.createFromSelection',
      groupId,
      selectedEntityIds: sel.selectedEntityIds,
      enteredGroupId: sel.enteredGroupId,
      layerId: store.getSnapshot().context.activeLayerId ?? layers[0]?.id ?? '',
    });
    canvasSelectionStore.send({ type: 'entity.clear' });
    canvasSelectionStore.send({ type: 'entity.selectOne', entityId: groupId });
  }, [store, layers]);

  const handleActivateDepExplorer = useCallback((entityId: string) => {
    uiStore.send({ type: 'depExplorer.activateAndFocus', entityId });
  }, [uiStore]);

  const handleEntityContextMenu = useCallback((entityId: string, e: React.MouseEvent) => {
    e.preventDefault();

    const ctx = store.getSnapshot().context;
    const allEntities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.connectors),
      ...Object.values(ctx.groups),
    ];
    const entity = allEntities.find(ent => ent.id === entityId);
    if (!entity) return;

    const tasksById = globalTasksStore.getSnapshot().context.tasksById;
    const groups = materializeGroupsFromStore(store, tasksById);
    const segments = materializeSegmentsFromStore(store, tasksById);

    const items = buildEntityMenuItems(entityId, entity, store, layers, groups, segments, {
      handleRemoveSelected,
      handleCreateGroup,
      handleGroupToggleCollapse,
      setGroupPickerState,
      handleCopy,
      handleCopyAsImage,
      handleCut,
      handleDuplicate,
      handleActivateDepExplorer,
    }, t);

    uiStore.send({ type: 'contextMenu.set', menu: { x: e.clientX, y: e.clientY, items } });
  }, [store, layers, handleRemoveSelected, handleCreateGroup, handleGroupToggleCollapse, uiStore, setGroupPickerState, handleCopy, handleCopyAsImage, handleCut, handleDuplicate, handleActivateDepExplorer, t]);

  // Edge context menu
  const { handleSegmentContextMenu } = useEdgeContextMenu(setContextMenu);

  const handleCanvasContextMenu = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-canvas-entity]') ||
        (e.target as HTMLElement).closest('[data-connector]')) return;

    e.preventDefault();

    const screenPos = { x: e.clientX, y: e.clientY };
    const items = buildCanvasMenuItems(store, uiStore, buildAddSubmenuItems(undefined, screenPos), handleCreateGroup, handlePaste, t);
    uiStore.send({ type: 'contextMenu.set', menu: { x: e.clientX, y: e.clientY, items } });
  }, [store, handleCreateGroup, uiStore, buildAddSubmenuItems, handlePaste, t]);

  return {
    setContextMenu,
    setGroupPickerState,
    groupPickerCurrentGroupIds,
    handleEntityContextMenu,
    handleSegmentContextMenu,
    handleCanvasContextMenu,
    handleCreateGroup,
    handleRemoveSelected,
    handleGroupToggleCollapse,
    buildAddSubmenuItems,
  };
}
